<template>
  <div class="list-picker">
    <select :value="modelValue" :disabled="loading" @change="selectValue">
      <option value="">{{ loading ? "Chargement…" : placeholder }}</option>
      <option v-if="modelValue && !knownValue" :value="modelValue">
        {{ modelValue }}
      </option>
      <option
        v-for="option in options"
        :key="option.id ?? option.name"
        :value="option.name"
      >
        {{ option.name }}
      </option>
      <option v-if="allowCreate" value="__new__">+ Ajouter une valeur</option>
    </select>
    <div v-if="creating" class="list-picker__create">
      <input
        ref="newValueInput"
        v-model.trim="newValue"
        type="text"
        :placeholder="createPlaceholder"
        @keydown.enter.prevent="createValue"
      />
      <button
        type="button"
        :disabled="saving || !newValue"
        @click="createValue"
      >
        {{ saving ? "Ajout…" : "Ajouter" }}
      </button>
      <button type="button" class="cancel" @click="cancelCreate">
        Annuler
      </button>
    </div>
    <small v-if="error" class="list-picker__error">{{ error }}</small>
  </div>
</template>

<script>
import { computed, nextTick, onMounted, ref } from "vue";
import axios from "axios";
import config from "@/config/config.js";

export default {
  name: "ListPicker",
  props: {
    modelValue: { type: String, default: "" },
    list: { type: String, required: true },
    placeholder: { type: String, default: "Sélectionner une valeur" },
    createPlaceholder: { type: String, default: "Nouvelle valeur" },
    allowCreate: { type: Boolean, default: false },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const options = ref([]);
    const loading = ref(false);
    const saving = ref(false);
    const creating = ref(false);
    const newValue = ref("");
    const error = ref("");
    const newValueInput = ref(null);
    const knownValue = computed(() =>
      options.value.some((option) => option.name === props.modelValue),
    );
    const headers = () => ({
      Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    });

    const normalizeOptions = (payload) => {
      const values = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.result)
          ? payload.result
          : [];
      return values
        .map((value, index) =>
          typeof value === "string"
            ? { id: value, name: value }
            : { id: value.id ?? index, name: value.name ?? value.label },
        )
        .filter((value) => value.name);
    };
    const loadOptions = async () => {
      loading.value = true;
      error.value = "";
      try {
        const response = await axios.get(
          `${config.apiUrl}/lists/${props.list}`,
          { headers: headers() },
        );
        options.value = normalizeOptions(response.data);
      } catch (requestError) {
        console.error(
          `Impossible de charger la liste ${props.list}`,
          requestError,
        );
        error.value = "La liste est momentanément indisponible.";
      } finally {
        loading.value = false;
      }
    };
    const selectValue = async (event) => {
      if (event.target.value !== "__new__") {
        emit("update:modelValue", event.target.value);
        return;
      }
      creating.value = true;
      await nextTick();
      newValueInput.value?.focus();
    };
    const cancelCreate = () => {
      creating.value = false;
      newValue.value = "";
      error.value = "";
    };
    const createValue = async () => {
      const name = newValue.value.trim();
      if (!name || saving.value) return;
      saving.value = true;
      error.value = "";
      try {
        const response = await axios.post(
          `${config.apiUrl}/lists/${props.list}`,
          { name },
          { headers: headers() },
        );
        const option = {
          id: response.data?.id ?? response.data?._key ?? name,
          name: response.data?.name ?? name,
        };
        options.value.push(option);
        emit("update:modelValue", option.name);
        cancelCreate();
      } catch (requestError) {
        console.error(
          `Impossible d’ajouter une valeur à la liste ${props.list}`,
          requestError,
        );
        error.value = "La valeur n’a pas pu être ajoutée.";
      } finally {
        saving.value = false;
      }
    };

    onMounted(loadOptions);
    return {
      cancelCreate,
      createValue,
      creating,
      error,
      knownValue,
      loading,
      newValue,
      newValueInput,
      options,
      saving,
      selectValue,
    };
  },
};
</script>

<style scoped>
.list-picker {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
}
.list-picker select,
.list-picker input {
  width: 100%;
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid #d5dce5;
  border-radius: 8px;
  background: #fff;
  color: #354153;
  font: inherit;
  outline: 0;
}
.list-picker select:focus,
.list-picker input:focus {
  border-color: #67ad96;
  box-shadow: 0 0 0 3px rgba(22, 138, 100, 0.09);
}
.list-picker__create {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 7px;
}
.list-picker__create button {
  min-height: 36px;
  padding: 0 11px;
  border: 1px solid #168a64;
  border-radius: 7px;
  background: #168a64;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}
.list-picker__create button.cancel {
  border-color: #d7dee6;
  background: #fff;
  color: #657183;
}
.list-picker__create button:disabled {
  opacity: 0.5;
  cursor: wait;
}
.list-picker__error {
  color: #b74640;
  font-size: 11px;
}
@media (max-width: 520px) {
  .list-picker__create {
    grid-template-columns: 1fr 1fr;
  }
  .list-picker__create input {
    grid-column: 1/-1;
  }
}
</style>
